import React, { useState, useMemo } from 'react';
import { X, ChevronDown, ChevronRight, CheckCircle, Clock, Users } from 'lucide-react';

// Resolve a display name from a participant's data object
const getParticipantName = (participant) => {
    const data = participant.data || {};
    const nameKeys = ['name', 'fullName', 'full_name', 'Name', 'FullName', 'firstName', 'first_name'];
    for (const key of nameKeys) {
        if (data[key]) return data[key];
    }
    const firstStr = Object.values(data).find(v => typeof v === 'string' && v.length > 1);
    return firstStr || participant.registrationId || participant._id;
};

const AmenitiesCheckInModal = ({ event, participants, onClose }) => {
    const [expanded, setExpanded] = useState(null);

    // All unique amenity names across event + categories
    const allAmenities = useMemo(() => Array.from(new Set([
        ...(event?.amenities || []),
        ...(event?.participantCategories || []).flatMap(c => c.amenities || [])
    ])), [event]);

    const checkedIn = useMemo(() =>
        participants.filter(p => p.isCheckedIn), [participants]);

    // Mirror the scan controller's getEventAmenities logic:
    // category amenities take priority; fall back to event-level
    const getEligibleAmenities = (participant) => {
        const catId = participant.categoryId;
        if (catId && event?.participantCategories?.length > 0) {
            const cat = event.participantCategories.find(c => c.id === catId);
            if (cat?.amenities?.length > 0) return cat.amenities;
        }
        return event?.amenities || [];
    };

    // Per-amenity stats
    const stats = useMemo(() => allAmenities.map(name => {
        // Deduplicate by _id to avoid counting the same participant twice
        const eligible = Array.from(
            new Map(
                checkedIn
                    .filter(p => getEligibleAmenities(p).includes(name))
                    .map(p => [String(p._id), p])
            ).values()
        );
        const redeemed = eligible.filter(p => {
            const am = p.amenities || {};
            return am[name]?.redeemed === true;
        });
        return {
            name,
            eligible,
            redeemedCount: redeemed.length,
            pendingCount: eligible.length - redeemed.length
        };
    }), [allAmenities, checkedIn]);

    const totalEligible = useMemo(() =>
        new Set(
            checkedIn.filter(p => getEligibleAmenities(p).length > 0).map(p => String(p._id))
        ).size, [checkedIn]);

    const toggleExpand = (name) =>
        setExpanded(prev => (prev === name ? null : name));

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-teal-600" />
                        <h2 className="text-lg font-semibold text-gray-900">Amenity Check-In Overview</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Summary bar */}
                <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-6 text-sm">
                    <div>
                        <span className="text-gray-500">Total checked-in: </span>
                        <span className="font-semibold text-gray-900">{checkedIn.length}</span>
                    </div>
                    <div>
                        <span className="text-gray-500">With any amenity assigned: </span>
                        <span className="font-semibold text-teal-700">{totalEligible}</span>
                    </div>
                </div>

                {/* Table */}
                <div className="flex-1 overflow-y-auto">
                    {allAmenities.length === 0 ? (
                        <div className="text-center py-16 text-gray-500 text-sm">
                            No amenities configured for this event.
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-gray-50 border-b border-gray-200 z-10">
                                <tr>
                                    <th className="text-left px-6 py-3 font-medium text-gray-600">Amenity</th>
                                    <th className="text-center px-4 py-3 font-medium text-gray-600">Eligible (checked-in)</th>
                                    <th className="text-center px-4 py-3 font-medium text-green-700">Redeemed</th>
                                    <th className="text-center px-4 py-3 font-medium text-orange-600">Pending</th>
                                    <th className="w-8 px-2 py-3"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {stats.map(({ name, eligible, redeemedCount, pendingCount }) => (
                                    <React.Fragment key={name}>
                                        <tr
                                            className={`hover:bg-gray-50 cursor-pointer transition-colors ${expanded === name ? 'bg-teal-50' : ''}`}
                                            onClick={() => eligible.length > 0 && toggleExpand(name)}
                                        >
                                            <td className="px-6 py-3 font-medium text-gray-900">{name}</td>
                                            <td className="px-4 py-3 text-center">
                                                <span className="inline-flex items-center justify-center gap-1 font-semibold text-teal-700">
                                                    <Users className="w-3.5 h-3.5" />
                                                    {eligible.length}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className="inline-flex items-center justify-center gap-1 text-green-700 font-medium">
                                                    <CheckCircle className="w-3.5 h-3.5" />
                                                    {redeemedCount}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className="inline-flex items-center justify-center gap-1 text-orange-600 font-medium">
                                                    <Clock className="w-3.5 h-3.5" />
                                                    {pendingCount}
                                                </span>
                                            </td>
                                            <td className="px-2 py-3 text-gray-400">
                                                {eligible.length > 0 && (
                                                    expanded === name
                                                        ? <ChevronDown className="w-4 h-4" />
                                                        : <ChevronRight className="w-4 h-4" />
                                                )}
                                            </td>
                                        </tr>

                                        {/* Expanded participant list */}
                                        {expanded === name && (
                                            <tr>
                                                <td colSpan={5} className="px-6 pb-3 pt-0 bg-teal-50">
                                                    <div className="rounded-lg border border-teal-200 bg-white divide-y divide-gray-100 max-h-56 overflow-y-auto">
                                                        {eligible.map(p => {
                                                            const am = p.amenities || {};
                                                            const isRedeemed = am[name]?.redeemed === true;
                                                            return (
                                                                <div key={p._id} className="flex items-center justify-between px-4 py-2 text-sm">
                                                                    <div>
                                                                        <span className="font-medium text-gray-900">
                                                                            {getParticipantName(p)}
                                                                        </span>
                                                                        {p.registrationId && (
                                                                            <span className="ml-2 text-gray-400 text-xs">{p.registrationId}</span>
                                                                        )}
                                                                    </div>
                                                                    {isRedeemed ? (
                                                                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                                                                            <CheckCircle className="w-3 h-3" />
                                                                            Redeemed
                                                                        </span>
                                                                    ) : (
                                                                        <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                                                                            <Clock className="w-3 h-3" />
                                                                            Pending
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AmenitiesCheckInModal;
